import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, PlayCircle, Star } from 'lucide-react';
import { CountdownTimer } from './components/CountdownTimer';
import { Hero } from './components/Hero';
import { ProductCarousel } from './components/ProductCarousel';
import { PricingPacks } from './components/PricingPacks';
import { Testimonials } from './components/Testimonials';
import { useScreenInit } from './useScreenInit.js';
export function App() {
  const screenInit = useScreenInit();
  useEffect(() => {
    if (screenInit?.section) {
      const element = document.getElementById(screenInit.section);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }
  }, [screenInit]);
  return <div className="min-h-screen bg-[#fdfaf6] font-sans selection:bg-[#eaddca]" data-id="element-0">
      <div className="absolute top-0 left-0 right-0 z-40" data-id="element-1">
        <CountdownTimer data-id="element-2" />
      </div>

      <Hero data-id="element-3" />

      {/* Pain Section */}
      <section className="py-16 px-4 bg-white pt-[0px] pb-[20px]" data-id="element-4">
        <div className="max-w-5xl mx-auto" data-id="element-5">
          <h2 className="text-3xl md:text-5xl font-black text-red-700 text-center mb-12 uppercase tracking-tighter" data-id="element-6">
            ¡Evitá estos errores costosos!
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7">
            {[{
            emoji: '🚪',
            title: 'EL CAOS DE QUEDARTE AFUERA',
            desc: 'Las llaves quedaron adentro, la puerta se cerró y no tenés cómo entrar. Una pesadilla que arruina tu día.'
          }, {
            emoji: '🪟',
            title: 'TERMINAR ROMPIENDO UNA VENTANA',
            desc: 'Por desesperación, una situación simple termina convirtiéndose en una reparación carísima y peligrosa.'
          }, {
            emoji: '💸',
            title: 'GASTOS INESPERADOS',
            desc: 'Una urgencia puede aparecer en cualquier momento y obligarte a pagar fortunas que no tenías previstas.'
          }, {
            emoji: '⚠️',
            title: 'CERRADURAS QUE FALLAN',
            desc: 'La llave no gira, se traba o deja de funcionar cuando más la necesitás. Quedás vulnerable y frustrado.'
          }, {
            emoji: '😫',
            title: 'DEPENDER SIEMPRE DE TERCEROS',
            desc: 'Por no saber, cualquier inconveniente te obliga a esperar horas y pagar lo que te pidan.'
          }].map((item, i) => <div key={i} className="p-6 bg-red-50 rounded-2xl border-2 border-red-200 hover:border-red-400 hover:shadow-xl transition-all duration-300 group" data-id="element-8">
                <div className="text-4xl mb-4 transform group-hover:scale-110 transition-transform" data-id="element-9">
                  {item.emoji}
                </div>
                <h3 className="text-xl font-black text-red-800 mb-3 uppercase tracking-tight leading-none" data-id="element-10">
                  {item.title}
                </h3>
                <p className="text-sm text-red-900 font-bold leading-relaxed opacity-80" data-id="element-11">
                  {item.desc}
                </p>
              </div>)}
          </div>

          {/* Quick Solutions Section */}
          <div className="mt-24" data-id="element-12">
            <h2 className="text-3xl md:text-5xl font-black text-green-700 text-center mb-12 uppercase tracking-tight leading-none" data-id="element-13">
              ¡Transformá tu realidad <br className="hidden md:block" data-id="element-14" /> con
              este material!
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-15">
              {[{
              emoji: '🔐',
              title: 'MÁXIMA SEGURIDAD Y TRANQUILIDAD',
              desc: 'Convertite en el guardián de tu hogar. Conocé cada secreto de tus cerraduras y protegé a tu familia como un profesional.'
            }, {
              emoji: '💰',
              title: 'AHORRO INTELIGENTE Y CONSTANTE',
              desc: 'Olvidate de las facturas abusivas. Resolvé vos mismo los problemas y ahorrá miles de pesos desde el primer día.'
            }, {
              emoji: '⚡',
              title: 'CONTROL TOTAL Y PREVENCIÓN',
              desc: 'Anticipate a cualquier problema. Vas a tener el ojo entrenado para detectar y arreglar fallas antes de que sean una crisis.'
            }, {
              emoji: '🛠️',
              title: 'HABILIDADES PARA TODA LA VIDA',
              desc: 'Aprendé un oficio noble y práctico. Son conocimientos valiosos que te van a servir a vos y a los tuyos para siempre.'
            }, {
              emoji: '🚀',
              title: 'LIBERTAD Y NUEVOS INGRESOS',
              desc: '¡Empezá tu propio camino! Adquirí una habilidad con altísima demanda y generá ingresos extra cuando vos quieras.'
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} whileHover={{
              scale: 1.05,
              backgroundColor: '#f0fdf4'
            }} className="p-8 bg-white rounded-[2rem] border-2 border-green-100 shadow-lg flex flex-col items-center text-center group transition-all relative overflow-hidden" data-id="element-16">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-green-50 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500" data-id="element-17" />

                  <motion.div animate={{
                y: [0, -5, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="text-5xl mb-6 transform group-hover:scale-110 transition-transform relative z-10" data-id="element-18">
                    {item.emoji}
                  </motion.div>
                  <h3 className="text-xl font-black text-green-800 mb-4 uppercase tracking-tight relative z-10 leading-tight" data-id="element-19">
                    {item.title}
                  </h3>
                  <p className="text-sm text-green-900 font-bold leading-relaxed relative z-10 opacity-90" data-id="element-20">
                    {item.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 pt-[0px] pb-[0px]" data-id="element-21">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-22">
          <div data-id="element-23">
            <h2 className="text-3xl md:text-4xl font-bold text-[#5d4037] mb-6" data-id="element-24">
              Con este libro vas a tener una guía clara para empezar
            </h2>
            <p className="text-lg text-[#8b6b61] leading-relaxed mb-6" data-id="element-25">
              Vas a encontrar explicaciones simples, conceptos prácticos y
              material organizado para entender la cerrajería del hogar y
              avanzar con seguridad, sin perderte entre información suelta.
            </p>
            <ul className="space-y-4" data-id="element-26">
              {['Conceptos prácticos', 'Explicaciones simples', 'Material organizado'].map((item, i) => <li key={i} className="flex items-center gap-3 text-[#5d4037] font-semibold" data-id="element-27">
                  <CheckCircle2 className="text-[#d4af37] w-6 h-6" data-id="element-28" />
                  {item}
                </li>)}
            </ul>
          </div>
          <div className="bg-[#eaddca] rounded-3xl p-8 shadow-inner" data-id="element-29">
            <div className="bg-white p-4 rounded-2xl shadow-xl rotate-3" data-id="element-30">
              <img src="https://cdn.magicpatterns.com/uploads/764zmXUNkWWzrvpCPDoDon/5_en_1.webp" alt="Pack" className="w-full h-auto" data-id="element-31" />
            </div>
          </div>
        </div>
      </section>

      {/* Bonus Section */}
      <section id="bonuses" className="py-20 px-4 bg-[#fdfaf6] pt-[0px] pb-[0px]" data-id="element-32">
        <div className="max-w-7xl mx-auto" data-id="element-33">
          <h2 className="text-3xl md:text-4xl font-bold text-[#5d4037] text-center mb-16" data-id="element-34">
            Bonus Exclusivos del Libro
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8" data-id="element-35">
            {[{
            title: 'Lockpicking Técnico',
            desc: 'Domina el arte de la apertura técnica con esta guía detallada.',
            img: 'https://cdn.magicpatterns.com/uploads/o2QTJx8A2nCAK8MNVu4hAS/bonus_1_lockpicking.webp'
          }, {
            title: 'Curso Rápido de Apertura',
            desc: 'Aprende los fundamentos esenciales en tiempo récord para empezar ya.',
            img: 'https://cdn.magicpatterns.com/uploads/bZvvUizEhdAJqGJYjzv9HZ/bonus_2_curso_rapido.webp'
          }, {
            title: 'La Biblia del Lockpicking',
            desc: 'El compendio definitivo con todos los secretos de la cerrajería moderna.',
            img: 'https://cdn.magicpatterns.com/uploads/coKSq1p7ZEXj1kKTZPXScK/Bonus_3_la_biblia_del_lock.webp'
          }, {
            title: 'Manual de Cerrojos',
            desc: 'Especialízate en la apertura y reparación de cerrojos de alta seguridad.',
            img: 'https://cdn.magicpatterns.com/uploads/rmgHWzZD1WiKsEKuMNaHMt/bonuis_4_cerrojos.webp'
          }, {
            title: 'Manual de Cerraduras',
            desc: 'Guía práctica paso a paso para enfrentar cualquier tipo de cerradura.',
            img: 'https://cdn.magicpatterns.com/uploads/5Uix8a1uvakTb6MP8mM7FX/bonus_5_manual_para_abrir_cerraduras.webp'
          }].map((bonus, i) => <motion.div key={i} whileHover={{
            y: -10
          }} className="bg-white rounded-3xl shadow-xl border-b-8 border-[#d4af37] overflow-hidden flex flex-col h-full" data-id="element-36">
                <div className="relative aspect-square overflow-hidden" data-id="element-37">
                  <img src={bonus.img} alt={bonus.title} className="w-full h-full object-cover" data-id="element-38" />
                  <div className="absolute top-4 right-4 bg-[#d4af37] text-white text-[10px] font-black px-2 py-1 rounded-full shadow-lg" data-id="element-39">
                    BONUS {i + 1}
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-grow text-center" data-id="element-40">
                  <h3 className="font-bold text-[#d4af37] text-lg leading-tight mb-2" data-id="element-41">
                    {bonus.title}
                  </h3>
                  <p className="text-black text-xs mb-4 flex-grow" data-id="element-42">
                    {bonus.desc}
                  </p>
                  <div className="mt-auto pt-4" data-id="element-43">
                    <span className="inline-block bg-[#d4af37] text-white text-xs font-black px-4 py-1.5 rounded-full shadow-md" data-id="element-44">
                      GRATIS HOY
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      <ProductCarousel data-id="element-45" />

      {/* Video Section */}
      <section className="py-20 px-4 bg-[#5d4037] text-white pt-[40px] pb-[40px]" data-id="element-46">
        <div className="max-w-4xl mx-auto text-center" data-id="element-47">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-48">
            Mirá cómo funciona el acceso
          </h2>
          <p className="text-lg opacity-90 mb-12" data-id="element-49">
            Todo el material se entrega de forma digital para que puedas empezar
            al instante.
          </p>
          <div className="relative aspect-video max-w-5xl mx-auto rounded-3xl overflow-hidden shadow-2xl border-4 border-white/20" data-id="element-50">
            <video src="https://cdn.magicpatterns.com/uploads/kso3gDiCqRnCzrM7BYqcqc/me_podes_crear_un_video_demost.mp4" className="w-full h-full object-cover" controls autoPlay muted loop playsInline data-id="element-51" />
          </div>
        </div>
      </section>

      <div id="pricing" data-id="element-52">
        <PricingPacks data-id="element-53" />
      </div>

      <Testimonials data-id="element-54" />

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-white pt-[0px] pb-[0px]" data-id="element-55">
        <div className="max-w-4xl mx-auto" data-id="element-56">
          <h2 className="text-3xl md:text-4xl font-bold text-[#5d4037] text-center mb-12" data-id="element-57">
            ¿Qué vas a lograr con este material?
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-58">
            {['Aprender desde cero', 'Tener una habilidad práctica', 'Estudiar a tu ritmo', 'Acceso inmediato', 'Material organizado y fácil de seguir', 'Ideal para sumar una salida laboral'].map((benefit, i) => <div key={i} className="flex items-center gap-4 p-4 bg-[#fdfaf6] rounded-xl border border-[#eaddca]" data-id="element-59">
                <CheckCircle2 className="text-green-500 w-6 h-6 shrink-0" data-id="element-60" />
                <span className="text-[#5d4037] font-medium" data-id="element-61">{benefit}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section id="guarantee" className="py-20 px-4 bg-[#fdfaf6] pt-[40px] pb-[40px]" data-id="element-62">
        <div className="max-w-3xl mx-auto text-center bg-white p-12 rounded-[3rem] shadow-xl border-2 border-[#eaddca]" data-id="element-63">
          <ShieldCheck className="w-20 h-20 text-[#d4af37] mx-auto mb-6" data-id="element-64" />
          <h2 className="text-3xl font-bold text-[#5d4037] mb-4" data-id="element-65">
            Compra segura
          </h2>
          <p className="text-xl text-[#8b6b61] mb-8" data-id="element-66">
            Recibís el acceso de forma inmediata y tenés garantía de 7 días.
          </p>
          <div className="flex justify-center gap-4" data-id="element-67">
            <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" className="h-8 opacity-50" data-id="element-68" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-8 opacity-50" data-id="element-69" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 bg-[#5d4037] text-white text-center" data-id="element-70">
        <div className="max-w-3xl mx-auto" data-id="element-71">
          <h2 className="text-4xl font-bold mb-8" data-id="element-72">
            ¿Listo para empezar tu nuevo oficio?
          </h2>
          <button onClick={() => {
          const target = document.getElementById('pricing');
          if (!target) return;
          const targetPosition = target.getBoundingClientRect().top + window.pageYOffset;
          const startPosition = window.pageYOffset;
          const distance = targetPosition - startPosition;
          const duration = 2000;
          let start: number | null = null;
          const step = (timestamp: number) => {
            if (!start) start = timestamp;
            const progress = timestamp - start;
            const percentage = Math.min(progress / duration, 1);
            const easing = percentage < 0.5 ? 4 * percentage * percentage * percentage : 1 - Math.pow(-2 * percentage + 2, 3) / 2;
            window.scrollTo(0, startPosition + distance * easing);
            if (progress < duration) window.requestAnimationFrame(step);
          };
          window.requestAnimationFrame(step);
        }} className="bg-[#d4af37] hover:bg-[#c4a030] text-white font-bold py-6 px-12 rounded-2xl text-2xl shadow-2xl transition-all transform hover:scale-105 active:scale-95 mb-8" data-id="element-73">
            ACCEDER AHORA
          </button>
          <p className="text-white/70 flex items-center justify-center gap-2" data-id="element-74">
            <Star className="w-4 h-4 fill-current" data-id="element-75" /> Más de 1,500 alumnos ya
            empezaron
          </p>
        </div>
      </section>

      <footer className="py-8 text-center text-[#8b6b61] text-sm border-t border-[#eaddca]" data-id="element-76">
        <p data-id="element-77">© 2024 Cerrajería Profesional. Todos los derechos reservados.</p>
      </footer>
    </div>;
}